'use client';
import { useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { uploadToS3, submitPDPLRequest } from '@/apis';

export default function ReportViolationPage() {
  const [reporterType, setReporterType] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [formFields, setFormFields] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    incidentDate: '',
    incidentLocation: '',
    description: '',
    affectedSubjects: '',
    severity: 'Low',
    files: [],
    consent: false,
  });

  const options = [
    { value: 'data-breach', label: 'Data breach' },
    { value: 'unauthorized-access', label: 'Unauthorized access' },
    { value: 'data-misuse', label: 'Data misuse' },
    { value: 'aml-fraud', label: 'AML / Fraud' },
    { value: 'insider-threat', label: 'Insider threat' },
    { value: 'other', label: 'Other' },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setFormFields((prev) => ({ ...prev, [name]: checked }));
    } else if (type === 'file') {
      setFormFields((prev) => ({ ...prev, files: Array.from(files) }));
    } else {
      setFormFields((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formFields.consent) {
      return Swal.fire('Error', 'Please confirm consent.', 'error');
    }

    try {
      // Upload files to S3
      const uploadedFiles = await Promise.all(
        formFields.files.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file);
          const res = await uploadToS3(formData);
          return { name: file.name, url: res.fileUrl };
        })
      );

      const payload = {
		formType: "ViolationReport",
        reporterType,
        reporterInfo:
          reporterType !== 'Anonymous'
            ? {
                name: formFields.name,
                email: formFields.email,
                phone: formFields.phone,
                role: formFields.role,
              }
            : null,
        incidentDateTime:  formFields.incidentDate,
        incidentLocation: formFields.incidentLocation,
        violationCategories: selectedOptions.map((opt) => opt.value),
        requestDescription: formFields.description,
        affectedSubjects: formFields.affectedSubjects,
        severity: formFields.severity,
        violationFiles: uploadedFiles,
      };

	  console.log(payload);

      const res = await submitPDPLRequest(payload);

      Swal.fire({
        icon: 'success',
        title: 'Report Submitted',
        html: `${res.message}<br>Case ID: <b style="color:blue">${res.caseId}</b>`,
        timer: 6000,
      });
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to submit report.', 'error');
    }
  };

  return (
    <section className='legacy-page bg-gray-50 text-gray-900 px-6 py-6 md:py-10'>
      <div className='container mx-auto max-w-4xl lg:max-w-5xl'>
        <form className='space-y-8' onSubmit={handleSubmit}>
          {/* Header */}
          <h1 className='text-3xl md:text-4xl font-bold text-primary mb-8'>
            Violation Reporting Portal
          </h1>

          {/* Reporter Type */}
          <div className='mb-8'>
            <h2 className='text-lg font-semibold mb-2'>Reporter Type <span className='text-red-500'>*</span></h2>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              {['Employee', 'Customer', 'Third-party', 'Anonymous'].map((type) => (
                <label key={type} className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='reporterType'
                    value={type}
                    className='w-4 h-4'
                    checked={reporterType === type}
                    onChange={(e) => setReporterType(e.target.value)}
                  />
                  {type}
                </label>
              ))}
            </div>
          </div>

          {/* Reporter Details */}
          {reporterType !== 'Anonymous' && reporterType !== '' && (
            <div className='mb-8'>
              <h2 className='text-lg font-semibold mb-2'>Reporter Information</h2>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                {['name','email','phone','role'].map((field) => (
                  <div key={field}>
                    <label className='block mb-1'>
                      {field.charAt(0).toUpperCase() + field.slice(1)}
                    </label>
                    <input
                      name={field}
                      value={formFields[field]}
                      onChange={handleChange}
                      className='w-full border p-2 rounded'
                      placeholder={`Your ${field}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Incident Details */}
          <div className='mb-8'>
            <h2 className='text-lg font-semibold mb-2'>Incident Details</h2>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block mb-1'>Incident Date & Time</label>
                <input
                  name='incidentDate'
                  type='datetime-local'
                  value={formFields.incidentDate}
                  onChange={handleChange}
                  className='w-full border p-2 rounded'
                />
              </div>
              <div>
                <label className='block mb-1'>System / Module / Location</label>
                <input
                  name='incidentLocation'
                  value={formFields.incidentLocation}
                  onChange={handleChange}
                  className='w-full border p-2 rounded'
                />
              </div>
            </div>
          </div>

          {/* Violation Category */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-2">Violation Category <span className='text-red-500'>*</span></h2>
            <Select
              isMulti
              options={options}
              value={selectedOptions}
              onChange={setSelectedOptions}
              placeholder="Select violation categories..."
              className="text-sm"
              classNamePrefix="react-select"
			  required
            />
          </div>

          {/* Description */}
          <div className='mb-8'>
            <h2 className='text-lg font-semibold mb-2'>Description of Violation <span className='text-red-500'>*</span></h2>
            <textarea
              name='description'
              value={formFields.description}
              onChange={handleChange}
              className='w-full border p-2 rounded'
              rows={4}
              required
              placeholder='Describe what happened'
            />
          </div>

          {/* Affected subjects */}
          <div className='mb-8'>
            <h2 className='text-lg font-semibold mb-2'>Affected Data Subjects</h2>
            <input
              name='affectedSubjects'
              value={formFields.affectedSubjects}
              onChange={handleChange}
              className='w-full border p-2 rounded'
              placeholder='Names or total count (optional)'
            />
          </div>

          {/* Severity */}
          <div className='mb-8'>
            <h2 className='text-lg font-semibold mb-2'>Severity</h2>
            <select
              name='severity'
              value={formFields.severity}
              onChange={handleChange}
              className='w-full border p-2 rounded'
            >
              <option>Low</option>
              <option>Medium</option>
              <option>High</option>
              <option>Critical</option>
            </select>
          </div>

          {/* File Upload */}
          <div className='mb-8'>
            <h2 className='text-lg font-semibold mb-2'>Upload Evidence</h2>
            <label className='inline-block bg-primary text-white px-4 py-2 rounded cursor-pointer'>
              Upload Files
              <input
                name='files'
                type='file'
                multiple
                accept='.pdf,.jpg,.jpeg,.png'
                className='hidden'
                onChange={handleChange}
              />
            </label>
            <p className='text-gray-500 text-sm mt-2'>
              Max size: 10MB per file • Total limit: 100MB • PDF/JPG/PNG allowed
            </p>
          </div>

          {/* Consent */}
          <div className='mb-6'>
            <label className='flex items-start gap-3 cursor-pointer'>
              <input
                type='checkbox'
                name='consent'
                checked={formFields.consent}
                onChange={handleChange}
                className='mt-1 w-4 h-4'
				required
              />
              <span className='leading-snug'>
                I confirm the information provided is accurate. I understand that if I choose
                anonymity, the organization may not be able to contact me for follow-up.
              </span>
            </label>
          </div>

          {/* Submit */}
          <div className='flex gap-4'>
            <button type='submit' className='bg-primary text-white px-6 py-2 rounded-md'>
              Submit Report
            </button>
            <button type='button' className='bg-gray-300 px-6 py-2 rounded-md' onClick={()=>window.location.reload()}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
